
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const ListingAutomationPage = () => {
  const features = [
    {
      icon: 'ri-camera-line',
      title: 'Auto Photo Enhancement',
      description: 'AI automatically enhances property photos for maximum visual appeal and engagement.'
    },
    {
      icon: 'ri-edit-line',
      title: 'Smart Descriptions',
      description: 'Generate compelling property descriptions that highlight key features and benefits.'
    },
    {
      icon: 'ri-price-tag-line',
      title: 'Dynamic Pricing',
      description: 'AI-powered pricing recommendations based on market data and comparable properties.'
    },
    {
      icon: 'ri-global-line',
      title: 'Multi-Platform Sync',
      description: 'Automatically distribute listings across MLS, Zillow, Realtor.com, and social media.'
    },
    {
      icon: 'ri-calendar-line',
      title: 'Scheduling Integration',
      description: 'Automated showing scheduling with calendar integration and client notifications.'
    },
    {
      icon: 'ri-bar-chart-line',
      title: 'Performance Analytics',
      description: 'Track listing performance with detailed analytics and optimization suggestions.'
    }
  ];

  const automationSteps = [
    {
      step: 1,
      title: 'Upload Property Data',
      description: 'Simply upload photos and basic property information',
      icon: 'ri-upload-line'
    },
    {
      step: 2,
      title: 'AI Processing',
      description: 'Our AI enhances photos, writes descriptions, and sets optimal pricing',
      icon: 'ri-brain-line'
    },
    {
      step: 3,
      title: 'Review & Approve',
      description: 'Review the generated listing and make any desired adjustments',
      icon: 'ri-eye-line'
    },
    {
      step: 4,
      title: 'Auto Distribution',
      description: 'Listing is automatically published across all connected platforms',
      icon: 'ri-send-plane-line'
    }
  ];

  const benefits = [
    {
      metric: '75%',
      label: 'Time Saved',
      description: 'Reduce listing creation time from hours to minutes'
    },
    {
      metric: '40%',
      label: 'More Views',
      description: 'AI-optimized listings get significantly more engagement'
    },
    {
      metric: '25%',
      label: 'Faster Sales',
      description: 'Properties sell faster with professional AI-enhanced listings'
    },
    {
      metric: '90%',
      label: 'Accuracy Rate',
      description: 'AI pricing recommendations are highly accurate'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(37, 99, 235, 0.8), rgba(37, 99, 235, 0.8)), url('https://public.readdy.ai/ai/img_res/322ef8b9ec5cdf11921e812c6d44df4c.jpg')`
        }}
      >
        <div className="container mx-auto px-6 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">
              Automated Listing Creation
            </h1>
            <p className="text-xl mb-8 text-blue-100">
              Create professional property listings in minutes, not hours, with AI-powered automation
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
                Try Free Demo
              </button>
              <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
                Watch Tutorial
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Proven Results</h2>
            <p className="text-xl text-gray-600">See the impact of AI-powered listing automation</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center bg-white p-8 rounded-xl shadow-lg">
                <div className="text-4xl font-bold text-blue-600 mb-3">{benefit.metric}</div>
                <h3 className="text-xl font-bold mb-3 text-gray-800">{benefit.label}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Powerful Automation Features</h2>
            <p className="text-xl text-gray-600">Everything you need to create stunning listings automatically</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-800">How Listing Automation Works</h2>
            <p className="text-xl text-gray-600">From upload to publication in 4 simple steps</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {automationSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-8">
                  <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${step.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-white border-2 border-blue-600 rounded-full flex items-center justify-center text-blue-600 font-bold text-sm">
                    {step.step}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-800">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Showcase */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-800">See the Difference</h2>
            <p className="text-xl text-gray-600">Compare manual vs AI-automated listings</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Before */}
            <div className="bg-red-50 p-8 rounded-xl">
              <h3 className="text-2xl font-bold mb-6 text-red-600 text-center">Manual Process</h3>
              <div className="space-y-4">
                <div className="flex items-center text-gray-700">
                  <i className="ri-time-line text-red-500 mr-3"></i>
                  <span>3-4 hours per listing</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <i className="ri-image-line text-red-500 mr-3"></i>
                  <span>Manual photo editing</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <i className="ri-edit-line text-red-500 mr-3"></i>
                  <span>Writing descriptions from scratch</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <i className="ri-calculator-line text-red-500 mr-3"></i>
                  <span>Manual pricing research</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <i className="ri-upload-line text-red-500 mr-3"></i>
                  <span>Individual platform uploads</span>
                </div>
              </div>
              <div className="mt-6 p-4 bg-red-100 rounded-lg">
                <p className="text-red-700 font-medium">Result: Inconsistent quality, time-consuming process</p>
              </div>
            </div>

            {/* After */}
            <div className="bg-green-50 p-8 rounded-xl">
              <h3 className="text-2xl font-bold mb-6 text-green-600 text-center">AI Automation</h3>
              <div className="space-y-4">
                <div className="flex items-center text-gray-700">
                  <i className="ri-time-line text-green-500 mr-3"></i>
                  <span>15 minutes per listing</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <i className="ri-magic-line text-green-500 mr-3"></i>
                  <span>AI-enhanced photos</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <i className="ri-robot-line text-green-500 mr-3"></i>
                  <span>AI-generated descriptions</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <i className="ri-brain-line text-green-500 mr-3"></i>
                  <span>AI pricing recommendations</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <i className="ri-send-plane-line text-green-500 mr-3"></i>
                  <span>Automatic multi-platform distribution</span>
                </div>
              </div>
              <div className="mt-6 p-4 bg-green-100 rounded-lg">
                <p className="text-green-700 font-medium">Result: Professional quality, lightning fast</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Seamless Integrations</h2>
            <p className="text-xl text-gray-600">Connect with your favorite platforms and tools</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-home-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="font-bold text-gray-800">MLS Systems</h3>
              <p className="text-gray-600 text-sm mt-2">Direct integration with major MLS platforms</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-global-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="font-bold text-gray-800">Listing Portals</h3>
              <p className="text-gray-600 text-sm mt-2">Zillow, Realtor.com, Trulia, and more</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-share-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="font-bold text-gray-800">Social Media</h3>
              <p className="text-gray-600 text-sm mt-2">Facebook, Instagram, LinkedIn posting</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-database-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="font-bold text-gray-800">CRM Systems</h3>
              <p className="text-gray-600 text-sm mt-2">Salesforce, HubSpot, Chime integration</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Automate Your Listings?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Save 75% of your time while creating better listings that sell faster
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              Start Free Trial
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              Schedule Demo
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ListingAutomationPage;
